
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Search, Plus, Edit, Copy, Trash2, Users, Play, Pause } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import FunnelCreateModal from "./FunnelCreateModal";
import FunnelEditModal from "./FunnelEditModal";

const FunnelsTab: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedFunnel, setSelectedFunnel] = useState(null);
  const { toast } = useToast();

  // Mock data - this would come from Supabase
  const funnels = [
    {
      id: "1",
      name: "New User Onboarding",
      description: "Welcome sequence for new signups",
      trigger_type: "signup",
      is_active: true,
      enrollment_count: 156,
      completion_rate: 68.5,
      created_at: "2024-01-15T10:00:00Z"
    },
    {
      id: "2",
      name: "Profile Completion Reminder",
      description: "Encourage users to complete their profiles",
      trigger_type: "profile_complete",
      is_active: true,
      enrollment_count: 89,
      completion_rate: 45.2,
      created_at: "2024-01-12T14:30:00Z"
    },
    {
      id: "3",
      name: "College Favorites Follow-up",
      description: "Engage users who favorite colleges",
      trigger_type: "college_favorite",
      is_active: false,
      enrollment_count: 234,
      completion_rate: 32.1,
      created_at: "2024-01-08T09:15:00Z"
    }
  ];

  const getTriggerColor = (trigger: string) => {
    switch (trigger) {
      case 'signup': return 'bg-green-100 text-green-800';
      case 'profile_complete': return 'bg-blue-100 text-blue-800';
      case 'college_favorite': return 'bg-purple-100 text-purple-800';
      case 'subscription_trial': return 'bg-orange-100 text-orange-800';
      case 'subscription_expired': return 'bg-red-100 text-red-800';
      case 'manual': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Handler functions
  const handleCreateFunnel = () => {
    setCreateModalOpen(true);
  };

  const handleSaveNewFunnel = async (funnelData: any) => {
    console.log('Creating new funnel:', funnelData);
    
    // If webhook URL is provided, test it
    if (funnelData.webhookUrl) {
      try {
        await fetch(funnelData.webhookUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          mode: "no-cors",
          body: JSON.stringify({
            event: "funnel_created",
            funnel_name: funnelData.name,
            timestamp: new Date().toISOString(),
          }),
        });
        
        toast({
          title: "Funnel Created",
          description: "Funnel created and Zapier webhook triggered successfully",
        });
      } catch (error) {
        toast({
          title: "Funnel Created",
          description: "Funnel created but webhook failed to trigger",
          variant: "destructive",
        });
      }
    } else {
      toast({
        title: "Funnel Created",
        description: "New sales funnel has been created successfully",
      });
    }
  };

  const handleEditFunnel = (funnelId: string) => {
    const funnel = funnels.find(f => f.id === funnelId);
    setSelectedFunnel(funnel);
    setEditModalOpen(true);
  };

  const handleSaveEditFunnel = async (funnelData: any) => {
    console.log('Updating funnel:', funnelData);
    
    // If webhook URL is provided, test it
    if (funnelData.webhookUrl) {
      try {
        await fetch(funnelData.webhookUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          mode: "no-cors",
          body: JSON.stringify({
            event: "funnel_updated",
            funnel_name: funnelData.name,
            timestamp: new Date().toISOString(),
          }),
        });
        
        toast({
          title: "Funnel Updated",
          description: "Funnel updated and Zapier webhook triggered successfully",
        });
      } catch (error) {
        toast({
          title: "Funnel Updated",
          description: "Funnel updated but webhook failed to trigger",
          variant: "destructive",
        });
      }
    } else {
      toast({
        title: "Funnel Updated",
        description: "Sales funnel has been updated successfully",
      });
    }
  };

  const handleCopyFunnel = (funnelId: string) => {
    const funnel = funnels.find(f => f.id === funnelId);
    console.log('Copying funnel:', funnel);
    toast({
      title: "Funnel Copied",
      description: `"${funnel?.name}" has been duplicated`,
    });
  };

  const handleToggleFunnel = (funnelId: string, currentStatus: boolean) => {
    console.log(`Toggling funnel ${funnelId} from ${currentStatus ? 'active' : 'inactive'}`);
    toast({
      title: currentStatus ? "Funnel Paused" : "Funnel Activated",
      description: `Funnel has been ${currentStatus ? 'paused' : 'activated'}`,
    });
  };

  const handleDeleteFunnel = (funnelId: string) => {
    const funnel = funnels.find(f => f.id === funnelId);
    console.log('Deleting funnel:', funnel);
    toast({
      title: "Funnel Deleted",
      description: `"${funnel?.name}" has been deleted`,
      variant: "destructive",
    });
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Sales Funnels</CardTitle>
            <Button onClick={handleCreateFunnel}>
              <Plus className="h-4 w-4 mr-2" />
              Create Funnel
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search funnels..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Trigger</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Enrollments</TableHead>
                <TableHead>Completion Rate</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {funnels.map((funnel) => (
                <TableRow key={funnel.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{funnel.name}</div>
                      <div className="text-sm text-muted-foreground">{funnel.description}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getTriggerColor(funnel.trigger_type)}>
                      {funnel.trigger_type.replace('_', ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={funnel.is_active ? "default" : "secondary"}>
                      {funnel.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      {funnel.enrollment_count}
                    </div>
                  </TableCell>
                  <TableCell>{funnel.completion_rate}%</TableCell>
                  <TableCell>
                    {new Date(funnel.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleEditFunnel(funnel.id)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleCopyFunnel(funnel.id)}>
                        <Copy className="h-4 w-4" />
                      </Button>
                      {funnel.is_active ? (
                        <Button variant="ghost" size="sm" onClick={() => handleToggleFunnel(funnel.id, funnel.is_active)}>
                          <Pause className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button variant="ghost" size="sm" onClick={() => handleToggleFunnel(funnel.id, funnel.is_active)}>
                          <Play className="h-4 w-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteFunnel(funnel.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      
      <FunnelCreateModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        onSave={handleSaveNewFunnel}
      />
      
      <FunnelEditModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        funnel={selectedFunnel}
        onSave={handleSaveEditFunnel}
      />
    </div>
  );
};

export default FunnelsTab;
